import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  Stack,
  Alert,
} from '@mui/material';
import {
  notAFitAuditService,
  AdminNotAFitRecord,
} from '../services/notAFitAuditService';

// FEAT-042 — admin Not a Fit Audit page (MVP-8.8).
// Read-only view of every active Not a Fit row plus optional historical
// rows. Used by the trust-score (FEAT-043) and abuse-investigation
// pipelines. No state-changing buttons — the action is silent, and the
// canonical reversal path is the marker's own settings UI.

const PAGE_SIZE = 25;

export const NotAFitAuditPage: React.FC = () => {
  const [items, setItems] = useState<AdminNotAFitRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markerFilter, setMarkerFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [includeUndone, setIncludeUndone] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await notAFitAuditService.list({
        markerId: markerFilter || undefined,
        targetId: targetFilter || undefined,
        includeUndone,
        page,
        limit: PAGE_SIZE,
      });
      setItems(result.items);
      setTotalPages(result.pagination.pages);
      setTotal(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit list');
    } finally {
      setLoading(false);
    }
  }, [markerFilter, targetFilter, includeUndone, page]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Not a Fit Audit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Active hides between two users. Used for trust-score signal
        collection and abuse investigation. Marking is silent by design —
        do not surface this page outside admin tooling.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Marker user ID"
              size="small"
              value={markerFilter}
              onChange={(e) => {
                setPage(1);
                setMarkerFilter(e.target.value);
              }}
              fullWidth
              data-testid="not-a-fit-marker-filter"
            />
            <TextField
              label="Target user ID"
              size="small"
              value={targetFilter}
              onChange={(e) => {
                setPage(1);
                setTargetFilter(e.target.value);
              }}
              fullWidth
              data-testid="not-a-fit-target-filter"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeUndone}
                  onChange={(_, value) => {
                    setPage(1);
                    setIncludeUndone(value);
                  }}
                  inputProps={{
                    'aria-label': 'Include undone (historical) rows',
                  }}
                />
              }
              label="Include undone"
            />
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {total === 0
                ? 'No Not a Fit rows match these filters.'
                : `${total} row${total === 1 ? '' : 's'} found.`}
            </Typography>
            <Table size="small" data-testid="not-a-fit-audit-table">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Marker</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} data-testid={`not-a-fit-audit-row-${row.id}`}>
                    <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{row.markerId}</TableCell>
                    <TableCell>{row.targetId}</TableCell>
                    <TableCell>
                      {row.undoneAt
                        ? `Undone ${new Date(row.undoneAt).toLocaleString()}`
                        : 'Active'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
              />
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default NotAFitAuditPage;
